import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { ROUTES } from './routes';

/**
 * Sentinel slug returned when the caller renders outside the slug-scoped
 * route tree and the active user has no membership (e.g. a brand-new
 * super-admin viewing `/profile` before joining any org). Keeps route
 * helpers from producing protocol-relative paths like `//runs`.
 */
const FALLBACK_ORG_SLUG = '_';

/**
 * Returns the active organization's slug.
 *
 * The URL is the source of truth (`OrgSlugResolver` sets `:orgSlug` from
 * `useParams`); when the caller is rendered outside the slug-scoped route
 * tree (e.g. profile/settings/admin pages or the marketing layout) we fall
 * back to the active membership's slug from `useAuth`, and finally to a
 * sentinel slug so route helpers always produce well-formed paths.
 */
export function useActiveOrgSlug(): string {
  const params = useParams<{ orgSlug?: string }>();
  const { activeMembership } = useAuth();
  return params.orgSlug || activeMembership?.organization.slug || FALLBACK_ORG_SLUG;
}

/**
 * Returns the org-scoped route helpers pre-bound to the active slug. Used
 * by every page/component that builds links to org-scoped URLs.
 */
export function useOrgRoutes() {
  const slug = useActiveOrgSlug();
  return useMemo(() => ROUTES.org(slug), [slug]);
}
